/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { useFormik } from 'formik';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormControl, FormHelperText, InputLabel, OutlinedInput } from '@mui/material';

import { HUModal2, HUModal2Props } from '../../../../components/HUModal2/HUModal2';
import { ArrowRoundedIcon } from '../../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../../i18n';
import { useFormStyles } from '../../../../styles/form';
import { ClusterUpdateRequestBody } from '../../../../types/cluster/ClusterUpdateRequestBody';
import { UpdateClusterDetailsParameterIdentification } from '../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { IProject } from '../../../../types/project/IProject';
import { useApiValidation } from '../../../../utils/useApiValidation';
import { useValidationTranslation } from '../../../../utils/useValidationTranslation';
import { initialFormValues, UpdateClusterDataForm, validationSchema } from './EditClusterDialog.miscellaneous';

type EditClusterDialogProps = Pick<HUModal2Props, 'open'> &
  Required<Pick<HUModal2Props, 'onCancel'>> & {
    editCluster: (data: ClusterUpdateRequestBody) => Promise<void>;
    relativeProjects: IProject[];
    identificationData: UpdateClusterDetailsParameterIdentification;
  };

export function EditClusterDialog(props: EditClusterDialogProps) {
  const { open, editCluster, relativeProjects, identificationData, onCancel } = props;
  const { projectId } = identificationData;

  const formClasses = useFormStyles();
  const { t } = useTranslation(Namespaces.Cluster);
  const { getFieldApiError, resetFieldApiError } = useApiValidation();
  const { vtr } = useValidationTranslation();

  const initialValues = useMemo<UpdateClusterDataForm>(
    () => ({
      ...initialFormValues,
      projectId: projectId,
    }),
    [projectId],
  );

  const onFormSubmit = (values: UpdateClusterDataForm) => {
    onCancel();
    void editCluster({
      metadata: { ...values },
    });
  };

  const formikProps = useFormik<UpdateClusterDataForm>({
    initialValues,
    validationSchema,
    onSubmit: onFormSubmit,
    enableReinitialize: true,
  });

  const { values, touched, errors, handleSubmit, resetForm, setFieldValue, submitForm, handleBlur } = formikProps;

  const selectedProject = useMemo(
    () => relativeProjects.find(p => p.id === values.projectId) ?? null,
    [relativeProjects, values.projectId],
  );

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  const getError = (field: keyof UpdateClusterDataForm): string => {
    return errors[field] || getFieldApiError(field) || '';
  };

  const hasError = (field: keyof UpdateClusterDataForm): boolean => {
    return !!touched[field] && !!getError(field);
  };

  const getLabelKey = (key: string): string => {
    return `details.header.edit_form.${key}`;
  };

  return (
    <HUModal2
      open={open}
      okButtonText={t('details.header.name.submit')}
      cancelButtonText={t('details.header.name.cancel')}
      okButtonProps={{ disabled: !formikProps.dirty }}
      onCancel={onCancel}
      onOk={submitForm}
      onClose={onCancel}
      size="big"
      disableRestoreFocus
      title={t(getLabelKey('title'))}
    >
      <form noValidate onSubmit={handleSubmit}>
        <FormControl
          className={cx(formClasses.formControl, formClasses.widthFull)}
          variant="outlined"
          error={hasError('projectId')}
        >
          <InputLabel htmlFor="projectId">{t(getLabelKey('project'))}</InputLabel>
          <HUAutocomplete
            popupIcon={<ArrowRoundedIcon />}
            renderInput={renderParams => (
              <OutlinedInput
                {...destructRenderInputParams(renderParams)}
                name="projectId"
                label={t(getLabelKey('project'))}
                onBlur={handleBlur}
              />
            )}
            options={relativeProjects}
            disableClearable={true}
            value={selectedProject ?? undefined}
            onBlur={handleBlur}
            openOnFocus
            onChange={(_e: any, value: IProject | null) => {
              if (value !== null) {
                void setFieldValue('projectId', value && value.id, true);
                resetFieldApiError('projectId');
              }
            }}
            getOptionLabel={v => v.name}
            data-cy="edit-cluster-dialog-project-input"
          />
          {hasError('projectId') && (
            <FormHelperText>
              {vtr(getError('projectId'), {
                field: t(getLabelKey('project')),
              })}
            </FormHelperText>
          )}
        </FormControl>
      </form>
    </HUModal2>
  );
}
